import api from '../../../api';

const API_URL = '/crm/invoices';

// Get all invoices with pagination and filters
export const getInvoices = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add pagination params
    if (params.page !== undefined) queryParams.append('page', params.page);
    if (params.size !== undefined) queryParams.append('size', params.size);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);
    
    // Add filter params
    const companyId = params.company_id ?? params.companyId;
    const projectId = params.project_id ?? params.projectId;
    const updatedById = params.uuser_id ?? params.updatedById;
    const invoiceTo = params.invoiceTo;
    const invoiceIssued = params.invoiceIssued;

    if (companyId) queryParams.append('company_id', companyId);
    if (params.leadId) queryParams.append('leadId', params.leadId);
    if (projectId) queryParams.append('project_id', projectId);
    if (updatedById) queryParams.append('uuser_id', updatedById);
    if (invoiceTo) queryParams.append('invoiceTo', invoiceTo);
    if (invoiceIssued) queryParams.append('invoiceIssued', invoiceIssued);
    // Handle multiple status values
    if (params.status) {
      if (Array.isArray(params.status)) {
        params.status.forEach(status => queryParams.append('status', status));
      } else {
        queryParams.append('status', params.status);
      }
    }
    if (params.assignedTo) queryParams.append('assignedTo', params.assignedTo);
    if (params.createdById) queryParams.append('createdById', params.createdById);
    if (params.serviceId) queryParams.append('serviceId', params.serviceId);
    if (params.departmentId) queryParams.append('departmentId', params.departmentId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.billDateFrom) queryParams.append('billDateFrom', params.billDateFrom);
    if (params.billDateTo) queryParams.append('billDateTo', params.billDateTo);
    if (params.dueDateFrom) queryParams.append('dueDateFrom', params.dueDateFrom);
    if (params.dueDateTo) queryParams.append('dueDateTo', params.dueDateTo);
    if (params.search) queryParams.append('search', params.search);
    if (params.viewOwnUserId) queryParams.append('viewOwnUserId', params.viewOwnUserId);
    if (params.viewDepartmentId) queryParams.append('viewDepartmentId', params.viewDepartmentId);
    
    const url = queryParams.toString() ? `${API_URL}?${queryParams}` : API_URL;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

// Get single invoice
export const getInvoice = async (id) => {
  try {
    const response = await api.get(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoice:', error);
    throw error;
  }
};

// Create new invoice
export const createInvoice = async (invoiceData) => {
  try {
    const response = await api.post(API_URL, invoiceData);
    return response.data;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

// Update invoice
export const updateInvoice = async (id, invoiceData) => {
  try {
    const response = await api.put(`${API_URL}/${id}`, invoiceData);
    return response.data;
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
};

// Delete invoice
export const deleteInvoice = async (id) => {
  try {
    const response = await api.delete(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
};

// Get invoice statistics
export const getInvoiceStats = async () => {
  try {
    const response = await api.get(`${API_URL}/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoice stats:', error);
    throw error;
  }
};

// Update invoice status
export const updateInvoiceStatus = async (id, status) => {
  try {
    const response = await api.patch(`${API_URL}/${id}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating invoice status:', error);
    throw error;
  }
};

// Get invoices by company
export const getInvoicesByCompany = async (companyId) => {
  try {
    const response = await api.get(`${API_URL}/company/${companyId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices by company:', error);
    throw error;
  }
};

// Get invoices by lead
export const getInvoicesByLead = async (leadId) => {
  try {
    const response = await api.get(`${API_URL}/lead/${leadId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices by lead:', error);
    throw error;
  }
};

// Get companies for dropdown - FIXED BASED ON PROSPECTS LOGIC
export const getCompanies = async () => {
  try {
    const res = await api.get('/crm/companies/options');
    return res.data;
  } catch (error) {
    console.error('Error fetching companies:', error);
    throw error;
  }
};

// Get leads for dropdown - FIXED BASED ON PROSPECTS LOGIC
export const getLeads = async (companyId = null) => {
  try {
    const url = companyId ? `/crm/leads/project-options?companyId=${companyId}` : '/crm/leads/project-options';
    const res = await api.get(url);
    return res.data;
  } catch (error) {
    console.error('Error fetching leads:', error);
    throw error;
  }
};

// Search functions
export const searchInvoicesByCompany = async (companyName) => {
  try {
    const response = await api.get(`${API_URL}/search/company?companyName=${encodeURIComponent(companyName)}`);
    return response.data;
  } catch (error) {
    console.error('Error searching invoices by company:', error);
    throw error;
  }
};

export const searchInvoicesByProject = async (projectName) => {
  try {
    const response = await api.get(`${API_URL}/search/project?projectName=${encodeURIComponent(projectName)}`);
    return response.data;
  } catch (error) {
    console.error('Error searching invoices by project:', error);
    throw error;
  }
}; 
// ========== INVOICE REVISION APIs ==========

// Create invoice revision
export const createInvoiceRevision = async (invoiceId, revisionData) => {
  try {
    const response = await api.post(`/crm/invoices/${invoiceId}/revisions`, revisionData);
    return response.data;
  } catch (error) {
    console.error('Error creating invoice revision:', error);
    throw error;
  }
};

// Get revisions by invoice
export const getInvoiceRevisions = async (invoiceId) => {
  try {
    const response = await api.get(`/crm/invoices/${invoiceId}/revisions`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoice revisions:', error);
    throw error;
  }
};

// Get pending revisions
export const getPendingRevisions = async () => {
  try {
    const response = await api.get('/crm/invoices/revisions/pending');
    return response.data;
  } catch (error) {
    console.error('Error fetching pending revisions:', error);
    throw error;
  }
};

// Approve revision
export const approveRevision = async (revisionId, comments) => {
  try {
    const response = await api.post(`/crm/invoices/revisions/${revisionId}/approve`, null, {
      params: comments ? { comments } : {}
    });
    return response.data;
  } catch (error) {
    console.error('Error approving revision:', error);
    throw error;
  }
};

// Reject revision
export const rejectRevision = async (revisionId, reason) => {
  try {
    const response = await api.post(`/crm/invoices/revisions/${revisionId}/reject`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error rejecting revision:', error);
    throw error;
  }
};
